import { useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import TripCoverSuggestions from "./TripCoverSuggestions";

/**
 * TripCardCoverButton
 * Sits on a coverless trip card. Reveals an inline 3-thumb Unsplash chooser and
 * on tap uploads the picked photo as a trip attachment and PATCHes the trip's
 * cover_id so the auto-hero is replaced by a real member-chosen cover.
 *
 * Props:
 *   trip     : { id, title, destination }
 *   onSaved  : () => void — parent refreshes the trip list after cover attached
 */
export default function TripCardCoverButton({ trip, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", "trip");
      fd.append("owner_id", trip.id);
      // 'photo' is in FILE_CATEGORIES; 'cover' would be coerced to 'other' server-side
      fd.append("category", "photo");
      fd.append("label", "Cover photo");
      const { data: att } = await api.post("/trips/attachments", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // Include title in the payload — save_trip previously required it even on partial updates
      await api.post("/trips", { id: trip.id, title: trip.title, cover: att.id });
      toast.success("Cover updated");
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set the cover");
    }
    setBusy(false);
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} disabled={busy}
        data-testid={`trip-card-pick-cover-${trip.id}`}
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-700 text-primary/85 hover:text-primary hover:underline">
        <ImagePlus className="w-3 h-3" /> Pick from Unsplash
      </button>
    );
  }

  return (
    <div className="mt-1 border border-zinc-200 bg-zinc-50 p-2" data-testid={`trip-card-cover-picker-${trip.id}`}>
      <div className="flex items-center justify-between text-[11px] font-700 text-zinc-700 mb-1">
        <span>Choose a cover</span>
        <button type="button" onClick={() => setOpen(false)} disabled={busy}
          data-testid={`trip-card-cover-picker-close-${trip.id}`}
          className="text-zinc-500 hover:text-destructive"><X className="w-3.5 h-3.5" /></button>
      </div>
      <TripCoverSuggestions
        query={trip.destination || trip.title}
        onPick={pick}
        testid={`trip-card-cover-suggestion-${trip.id}`}
      />
    </div>
  );
}
